/**
 * Reading a chat back from the GATEWAY, rather than from what a browser tab
 * happened to see.
 *
 * DIME's transcripts have always been client-authored: a tab renders the
 * frames of a run and saves the result. Nothing else writes them, so anything
 * that happens while no tab is watching — a reply that finishes after the
 * socket drops, a scheduled task committing its result, a run that continues
 * in a chat the user switched away from — never enters the record, and every
 * other device inherits the gap. That is the "my other device shows an older
 * version of this chat" report.
 *
 * openclaw keeps the authoritative transcript in its own store and serves it
 * over `chat.history`. That answer IS the conversation, and this module's one
 * job is to turn its rows into the bubbles the UI renders: drop tool rows,
 * group a turn's assistant steps into one bubble, strip the framing DIME
 * itself prepends to user turns.
 *
 * One job, and pure, so it can be tested without a socket. There is
 * deliberately no merge: the browser holds no second copy of a conversation
 * to reconcile against. It caches this answer verbatim so the next open of
 * that chat paints instantly, and the next read replaces the cache wholesale.
 * See the note at the foot of the file for what that removed and why.
 */

import { TOOL_CALLS_WINDOW, TOOL_NAME_MAX_CHARS } from './chat-history-storage';
import { isRenderableImageType, mediaTypeFromRef } from './media-attachments';
import type { ChatMessage, MessageAttachment, ToolCall } from './types';

/**
 * One page of `chat.history`, as the gateway answers it.
 *
 * `chat.history` is PAGED, and the page it returns is bounded three ways
 * independently of what was asked for: `limit` (1000 max, counted in visible
 * messages), a 6 MB response budget that discards OLDEST-FIRST, and
 * openclaw's own notion of which transcript branch is current. So a page is
 * never evidence of a conversation's beginning — only `hasMore: false` is.
 *
 * `sessionId` is the backing transcript identity, not the session key. Offsets
 * belong to one transcript, so a page whose `sessionId` differs from the one
 * paging started on has crossed a reset or a branch switch and its rows must
 * not be spliced onto the earlier ones.
 */
export interface GatewayHistoryPage {
  readonly messages?: readonly GatewayHistoryRow[];
  /** More rows exist before this page. Absent on gateways without paging. */
  readonly hasMore?: boolean;
  /** Offset to request for the page BEFORE this one. */
  readonly nextOffset?: number;
  /** Total visible messages the gateway holds for this transcript. */
  readonly totalMessages?: number;
  readonly sessionId?: string;
  readonly sessionInfo?: { readonly sessionId?: string };
}

/** The transcript identity a page belongs to, however the gateway reports it. */
export function pageTranscriptId(page: GatewayHistoryPage): string | null {
  const id = page.sessionInfo?.sessionId ?? page.sessionId;
  return id != null && id.trim().length > 0 ? id.trim() : null;
}

/**
 * One row of a `chat.history` response. Every field is optional on purpose:
 * this crosses a version boundary (the gateway is upgraded independently of
 * this client), and a row we cannot read must degrade to "skip it" rather
 * than throw inside a hydration that owns the whole transcript.
 */
export interface GatewayHistoryRow {
  readonly role?: string;
  readonly content?:
    | string
    | ReadonlyArray<{
        readonly type?: string;
        readonly text?: string;
        readonly mimeType?: string;
        readonly fileName?: string;
        /**
         * The file itself, when the agent echoes it back — base64, or an
         * already-formed `data:` URL. Present on the way OUT for every
         * upload; whether a given gateway build returns it on the way back is
         * what decides between a thumbnail and a labelled chip.
         */
        readonly content?: string;
      }>;
  readonly timestamp?: number;
  /**
   * What a tool row calls the tool it ran. Three spellings because the
   * gateway uses three — `toolName` on a `toolResult`, `tool` on a `tool`
   * row, `name` elsewhere — and `toolNameFromRow` reads whichever is there.
   *
   * Declared rather than reached for through a cast: the reader consumes
   * these fields, so the type that describes a row has to admit they exist.
   * Leaving them off did not stop the reader working — it stopped the tests
   * from compiling, which is how a red `tsc -p tsconfig.test.json` sat in CI
   * while the package's own typecheck passed.
   */
  readonly toolName?: string;
  readonly tool?: string;
  readonly name?: string;
  /**
   * The gateway's metadata envelope. `id` is the transcript entry identity —
   * stable across re-reads, unlike an array index, so it anchors a bubble
   * through repeated hydrations.
   */
  readonly __openclaw?: {
    readonly id?: string;
    /**
     * Files attached to the turn, as openclaw records them. Observed on a
     * live pod; see `readRowContent`, which is the only reader of it.
     */
    readonly media?: ReadonlyArray<{
      readonly url?: string;
      readonly contentType?: string;
      readonly kind?: string;
      readonly sizeBytes?: number;
    }>;
    /** See `sendKeyFromRow`. Same value as the sibling field below. */
    readonly idempotencyKey?: string;
    /**
     * The run this row belongs to. openclaw adopts the sending client's
     * `chat.send` key as the run id, so on an assistant row this is the same
     * string as the user row's `idempotencyKey`.
     */
    readonly runId?: string;
  };
  /**
   * The key the sending client put on its `chat.send`, echoed back. See
   * `sendKeyFromRow`.
   */
  readonly idempotencyKey?: string;
}

/**
 * The `chat.send` key the row came from, or undefined for a row no client of
 * this build sent.
 *
 * This is the one exact answer to "is this row the message already on
 * screen?". A message the user types is painted immediately, under an id the
 * browser invents, because waiting for the agent to confirm it would make
 * the chat feel broken. openclaw then broadcasts the same turn back with an
 * id of its own — two ids for one message, and nothing in common to compare.
 * Except this: the client stamps every send with a key, and openclaw returns
 * it on the row (in two places, either of which may be the one a given build
 * fills in) with the author's role appended — `msg-1788884386310-tbbq5g`
 * comes back as `msg-1788884386310-tbbq5g:user`. Taking that suffix off
 * leaves the key the sender still holds.
 *
 * Suffix, not "everything after a colon": the keys this client mints
 * (`createAgentWebSocket`) contain no colon, so a trailing `:word` is always
 * openclaw's addition and never part of the key.
 */
export function sendKeyFromRow(
  row: GatewayHistoryRow | undefined,
): string | undefined {
  const raw = row?.idempotencyKey ?? row?.__openclaw?.idempotencyKey;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  return raw.replace(/:[^:]*$/u, '');
}

/** Framing this client adds to outbound turns, so hydration can take it off. */
export interface GatewayHistoryFraming {
  /** The host's static preamble (`TerminalConfig.systemPreamble`). */
  readonly systemPreamble?: string;
  /**
   * Language directives to strip. Plural because the setting can CHANGE: a
   * chat carries whichever directive was current when each turn was sent, so
   * stripping only today's would leave yesterday's in the bubble. Callers
   * pass every directive the app can produce.
   */
  readonly languageDirectives?: readonly string[];
}

/**
 * Roles that carry a bubble's TEXT. Everything else a transcript holds is a
 * step within a turn rather than a turn: openclaw's own history for one
 * customer turn was 15 assistant steps interleaved with tool results, where
 * this UI shows one answer.
 */
const RENDERED_ROLES = new Set(['user', 'assistant']);

/**
 * Rows that record the agent using a tool. Matched loosely — `tool`,
 * `toolResult`, `tool_call` and whatever openclaw adds next all describe the
 * same thing to a reader — because the alternative is a bubble silently
 * losing its "ran a command" detail every time the gateway names a step
 * differently.
 */
function isToolRow(role: string): boolean {
  return role.toLowerCase().includes('tool');
}

/**
 * The tool a row says it ran.
 *
 * `toolName` and `tool` are the fields the LIVE path already trusts for
 * exactly this (`toolNameFromFrame` in the frame reducer), and history comes
 * from the same gateway, so this is its vocabulary rather than a guess.
 * `name` is accepted too, since a row is a record rather than an event.
 *
 * A row that names nothing contributes nothing: the log says which tools ran,
 * and an entry that cannot say which is noise in the shape of information.
 */
function toolNameFromRow(row: GatewayHistoryRow): string | undefined {
  const raw = row as unknown as Record<string, unknown>;
  for (const key of ['toolName', 'tool', 'name']) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim().slice(0, TOOL_NAME_MAX_CHARS);
    }
  }
  return undefined;
}

/**
 * Whether a content block is a FILE, as opposed to a step in the turn.
 *
 * A row's content array holds several kinds of thing: the answer's text, the
 * model's reasoning, each tool the agent called, and — sometimes — a file.
 * Only the last is an attachment.
 *
 * This used to be "anything that is not text", which is how a turn's own
 * reasoning and every tool call it made became anonymous "file" chips hanging
 * off the answer. They were invisible only because a second bug was throwing
 * every attachment away before it could be drawn.
 *
 * So a block has to LOOK like a file: say it is one, or carry a type or an
 * address for it. That is what an unknown-but-real attachment shape will have
 * (all three seen so far do), and what a step in the turn will not.
 */
function isFileBlock(block: {
  readonly type?: string;
  readonly mimeType?: string;
}): boolean {
  const type = block.type;
  if (type == null) return false;
  if (FILE_BLOCK_TYPES.has(type)) return true;
  const loose = block as { mimeType?: unknown; url?: unknown };
  return (
    typeof loose.mimeType === 'string' ||
    typeof loose.url === 'string' ||
    typeof (block as { attachment?: unknown }).attachment === 'object'
  );
}

/** Block types that are a file by name, whatever else they carry. */
const FILE_BLOCK_TYPES = new Set([
  'image',
  'file',
  'attachment',
  'document',
  'media',
  'audio',
  'video',
]);

/**
 * The operator-identity line this client writes as a chat message. It is
 * hidden from the visible transcript on the way out (`sendMessage` bypasses
 * the composer for it), so it must stay hidden on the way back in.
 */
const SESSION_CONTEXT_PREFIX = '<session-context>';

/** How `buildSendPayload` joins its parts. Splitting needs the same string. */
const PAYLOAD_SEPARATOR = '\n\n';

/** Visible text of a row, plus any image attachments it carries. */
/**
 * The text and the files on one transcript row.
 *
 * Exported because the LIVE path needs the same answer. It had its own inline
 * reader — text blocks joined, `type === 'image'` blocks mapped to a bare
 * `{type, mimeType}` — which knew none of the shapes this one does: not the
 * address a file is served from, not its name, not a document nested under an
 * `attachment` key. So the same message rendered one way while it streamed and
 * another way when the chat was reopened, and a file could be shown twice
 * because the two readers disagreed about what identified it.
 *
 * One reader for both paths is the point. A new shape then appears in one
 * place, not two.
 */
export function readGatewayRowContent(row: GatewayHistoryRow): {
  text: string;
  attachments?: MessageAttachment[];
} {
  const raw = row.content;
  // A row's text is either the whole `content` or the text blocks within it —
  // and its ATTACHMENTS are somewhere else entirely (see the media list at the
  // foot of this function). So neither shape may return early: a live pod
  // sends the text of an image turn as a plain string, and returning there is
  // exactly what hid the file.
  const blocks = Array.isArray(raw) ? raw : [];
  const text =
    typeof raw === 'string'
      ? raw
      : blocks
          .filter((b) => b?.type === 'text' && typeof b.text === 'string')
          .map((b) => b.text!)
          .join('');
  // Every attachment, not just images. The send path maps an image upload to
  // `type: 'image'` and EVERY OTHER upload to `type: 'file'` — a PDF, a CSV,
  // a log — so matching images alone left a file-only turn with no text and no
  // attachments, which is a turn that projects to nothing and a message the
  // user can watch disappear.
  //
  // Anything that is not text and names itself is an attachment. Loose on
  // purpose: the block types are the gateway's to extend, and a turn losing
  // its file because a new one appeared is the failure this fixes.
  //
  // A thumbnail when the agent kept the bytes.
  //
  // The send path uploads the whole file — `{type, mimeType, fileName,
  // content}`, base64 — so the agent HAS the image, and a history row that
  // echoes that payload back can be rendered as the picture it is. This code
  // used to read only the type and the mime type off a block, which is why a
  // refresh turned a screenshot into a grey chip: the only copy of the
  // picture was the `data:` URL held by the tab that picked the file, and it
  // died with the tab.
  //
  // Only `data:image/…` is built, and only for an image block. That is the
  // one shape the renderer accepts (an off-origin URL in an `<img src>` here
  // would issue a tracking GET), and it is the same restriction the storage
  // sanitizer applies. A row that carries no payload still renders a labelled
  // chip — which is then a statement about what the agent kept, not about
  // which device you are on.
  const attachments: MessageAttachment[] = [];
  for (const rawBlock of blocks) {
    const type = rawBlock?.type;
    if (typeof type !== 'string') continue;
    if (!isFileBlock(rawBlock)) continue;
    // TWO SHAPES FOR THE SAME THING. A picture the agent sends arrives with
    // its fields on the block itself; a document arrives with them nested one
    // level down:
    //
    //   { type: "attachment", attachment: { url, kind: "document",
    //       label: "Dime Terminal Summary.pdf", mimeType: "application/pdf" } }
    //
    // Reading only the outer level is why a PDF the agent sent rendered as an
    // unnamed chip that led nowhere: every field this needs was one level
    // below where it was looking. So the nested object, when there is one, is
    // what the fields are read from.
    const nested = (rawBlock as { attachment?: unknown }).attachment;
    const block = (
      nested != null && typeof nested === 'object' ? nested : rawBlock
    ) as typeof rawBlock;
    const mimeType =
      typeof block.mimeType === 'string' && block.mimeType.length > 0
        ? block.mimeType
        : type === 'image'
          ? 'image/*'
          : 'application/octet-stream';
    // The MIME TYPE decides whether this is a picture, not the block type.
    // The gateway stores an uploaded PNG as a generic file block — so keying
    // off the block type labelled a screenshot "file" and refused it a
    // thumbnail, while the very same field said `image/png`. Observed on a
    // live pod: a turn with an attached PNG renders as a bare `file` chip
    // after a reload.
    const isImage = isRenderableImageType(mimeType) || type === 'image';
    const payload =
      (block as { content?: unknown; data?: unknown }).content ??
      (block as { data?: unknown }).data;
    const previewUrl =
      isImage &&
      typeof payload === 'string' &&
      payload.length > 0 &&
      mimeType.startsWith('image/')
        ? // Already a data URL on some gateways, raw base64 on others; both
          // arrive here as a string and only one of them needs the prefix.
          payload.startsWith('data:')
          ? payload
          : `data:${mimeType};base64,${payload}`
        : undefined;
    // WHERE THE FILE IS, and WHAT IT IS CALLED — for a file the AGENT made.
    //
    // A picture the agent produced is recorded very differently from one the
    // user attached: it is a block of its own, carrying the address the agent
    // publishes it at and the name it saved it under:
    //
    //   { "type": "image",
    //     "url": "/api/chat/media/outgoing/<session>/<artifact>/full",
    //     "alt": "dime-red-primary.png", "mimeType": "image/png" }
    //
    // Reading neither field is why three generated logos rendered as three
    // anonymous "image" chips: with no address there is nothing to fetch, and
    // with no name there is nothing to say either. `alt` is the file's name
    // here rather than a description, which is why it stands in for one.
    const blockUrl = (block as { url?: unknown }).url;
    // Three names for the file's name, because the gateway uses a different
    // one per shape: `fileName` on an upload, `alt` on a picture the agent
    // sent, `label` on a document it sent. First one present wins.
    const fileName = [
      (block as { fileName?: unknown }).fileName,
      (block as { alt?: unknown }).alt,
      (block as { label?: unknown }).label,
    ].find(
      (candidate): candidate is string =>
        typeof candidate === 'string' && candidate.length > 0,
    );
    attachments.push({
      type: isImage ? 'image' : 'file',
      mimeType,
      ...(fileName != null ? { fileName } : {}),
      ...(typeof blockUrl === 'string' && blockUrl.length > 0
        ? { sourceUrl: blockUrl }
        : {}),
      ...(previewUrl != null && previewUrl.startsWith('data:image/')
        ? { previewUrl }
        : {}),
    });
  }
  // WHERE THE AGENT ACTUALLY RECORDS AN ATTACHMENT.
  //
  // Not in the content blocks — read off a live pod, a turn with an attached
  // image carries its text as a plain string and the file under
  // `__openclaw.media`:
  //
  //   "media": [{ "url": "media://inbound/816cf3a3….jpg",
  //               "contentType": "image/jpeg",
  //               "kind": "image", "sizeBytes": 39729 }]
  //
  // which is why a reopened chat showed the message with no sign of the file:
  // this reader was looking at the content array, and there was nothing there
  // to find. The bytes live on the pod under `~/.openclaw/media`.
  const media = row.__openclaw?.media;
  if (Array.isArray(media)) {
    for (const item of media) {
      if (item == null || typeof item !== 'object') continue;
      const ref = typeof item.url === 'string' ? item.url : '';
      // Three sources for "what is this file", in falling order of authority:
      // what the agent said it is, what the agent called it, and failing both
      // the extension on the reference itself. All three are needed because
      // only the last one is always present — and getting this wrong is not
      // cosmetic: a file not recognised as a picture is never fetched, so the
      // user sees a grey chip where their own photo should be.
      const contentType =
        typeof item.contentType === 'string' && item.contentType.length > 0
          ? item.contentType
          : (mediaTypeFromRef(ref) ?? 'application/octet-stream');
      // One predicate for both sources, so SVG cannot be a renderable image
      // on the strength of the gateway naming that type while the
      // extension-derived path refuses it.
      const isImageMedia =
        item.kind === 'image' || isRenderableImageType(contentType);
      attachments.push({
        type: isImageMedia ? 'image' : 'file',
        mimeType: contentType,
        ...(ref.length > 0 ? { sourceUrl: ref } : {}),
      });
    }
  }
  if (attachments.length === 0) return { text };
  return { text, attachments: dedupeAttachments(attachments) };
}

/**
 * A short, stable fingerprint of a row's own content.
 *
 * For the fallback identity of a row the gateway named nothing: position
 * alone is not enough, because position is PAGE-relative. A gateway that
 * omits ids gave the first row of the tail and the first row of an older page
 * the same identity, and the duplicate check that splices a fetched page onto
 * the transcript then discarded the whole page as already-seen — scroll-back
 * silently did nothing on exactly the gateways that need the fallback.
 *
 * Not a cryptographic hash and does not need to be: it distinguishes rows,
 * and two rows with the same role, time and text are the same row as far as
 * anything here can tell.
 */
function rowFingerprint(role: string, text: string): string {
  let h = 0x811c9dc5;
  const s = `${role} ${text}`;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * One entry per file, keeping the first — which is the better one.
 *
 * A row can report the same file in BOTH of the places the gateway records
 * files: as a content block, and in the row's own media list. Both shapes
 * have been seen on a live pod for the same upload, and a row carrying both
 * rendered the file twice — once as a named thumbnail and once as an
 * anonymous grey chip beside it.
 *
 * First wins because the content blocks are read first and carry more: the
 * block shape has the file's NAME, which the media list does not.
 *
 * Keyed on the address, then on name-and-type for the shapes that carry no
 * address. Two genuinely different files agree on neither.
 */
function dedupeAttachments(
  attachments: readonly MessageAttachment[],
): MessageAttachment[] {
  if (attachments.length < 2) return [...attachments];
  const seen = new Set<string>();
  const out: MessageAttachment[] = [];
  for (const a of attachments) {
    const key =
      a.sourceUrl != null && a.sourceUrl.length > 0
        ? `url:${a.sourceUrl}`
        : `name:${a.fileName ?? ''}|${a.mimeType}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/**
 * Take DIME's own framing off a user turn.
 *
 * `buildSendPayload` prepends the host preamble and the language directive to
 * every non-command turn, joined by a blank line, and the gateway stores that
 * whole string as the user message — which is why a raw `chat.history` read
 * of a Paradigm chat opens with "You are an analyst assistant for Paradigm…"
 * rather than what the user typed. The composer never showed it and neither
 * should a rehydrated bubble.
 *
 * Prefix-anchored and exact: a message that merely QUOTES the preamble keeps
 * it, because only a leading occurrence followed by the separator can be the
 * framing this client added. Trailing `messageContext` (the hidden data a
 * `/analyze` turn carries) is NOT stripped — it is per-message and unknown at
 * hydration time. Noted rather than guessed at: a heuristic that trimmed
 * trailing blocks would eat real message text.
 */
export function stripOutboundFraming(
  text: string,
  framing: GatewayHistoryFraming,
): string {
  let out = text;
  const prefixes = [
    framing.systemPreamble,
    ...(framing.languageDirectives ?? []),
  ];
  // Order-independent, and each prefix is taken at most once: the directive
  // follows the preamble on the wire, but a caller listing them the other way
  // round should not silently fail.
  let moved = true;
  while (moved) {
    moved = false;
    for (const prefix of prefixes) {
      if (prefix == null || prefix.trim().length === 0) continue;
      const candidate = prefix + PAYLOAD_SEPARATOR;
      if (out.startsWith(candidate)) {
        out = out.slice(candidate.length);
        moved = true;
      }
    }
  }
  return out;
}

/**
 * Gateway rows to bubbles.
 *
 * Turn grouping is the substance of this function. A single answer is many
 * rows in the store — assistant text, a tool call, more assistant text — and
 * DIME renders that as ONE bubble, because live streaming accumulates a run's
 * deltas into one message. So consecutive assistant rows (tool rows dropped
 * from between them) join into one bubble, separated by a blank line, and
 * anchored to the FIRST row's id and timestamp: first, so the bubble keeps
 * its place after the user message that prompted it, and keeps the same
 * identity when a later read finds the turn has grown.
 */
export function projectGatewayHistory(
  rows: readonly GatewayHistoryRow[] | undefined,
  framing: GatewayHistoryFraming = {},
): readonly ChatMessage[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out: ChatMessage[] = [];
  let openAssistant: {
    id: string;
    timestamp: number;
    parts: string[];
    attachments: MessageAttachment[];
    tools: string[];
  } | null = null;

  const closeAssistant = () => {
    if (openAssistant == null) return;
    const content = openAssistant.parts.join(PAYLOAD_SEPARATOR).trim();
    const { tools } = openAssistant;
    // The tool log, bounded the same way the live path bounds it: a window
    // over the tail, with the overflow counted rather than dropped silently.
    const overflow = Math.max(0, tools.length - TOOL_CALLS_WINDOW);
    const toolCalls: ToolCall[] = tools
      .slice(overflow)
      .map((name) => ({ name }));
    // A turn with no text, no attachment and no tool it can name leaves no
    // bubble: an empty one renders as an empty bubble, which is worse than
    // the absence it stands for. A turn that DID run tools is a real turn
    // even with nothing said — that is an agentic step, and the live view
    // shows it, so a re-read has to as well or the answer loses its history.
    // TWO ROWS, not one. The tool log is its own transcript entry — that is
    // how the live view builds it, and `MessageBubble` reads a row carrying
    // `toolCalls` as being ONLY that log: it returns the collapsed
    // "Ran 4 commands" block and never looks at the row's text or files.
    //
    // Putting both on one row therefore deleted the answer. A turn that made
    // a PDF always runs tools — it writes the file, it execs something — so
    // every turn that produced a file lost the file AND the sentence
    // announcing it, and the chat showed a bare "Ran 4 commands" where the
    // answer had been. It rendered live (the streamed answer carries no tool
    // log) and vanished on reopening, which is what made it look like history
    // was being lost.
    if (toolCalls.length > 0) {
      out.push({
        // Derived from the turn's own id, so a re-read produces the same row
        // rather than a second one beside it.
        id: `${openAssistant.id}:tools`,
        role: 'assistant',
        content: '',
        timestamp: openAssistant.timestamp,
        toolCalls,
        ...(overflow > 0 ? { toolCallsDropped: overflow } : {}),
      });
    }
    if (content.length > 0 || openAssistant.attachments.length > 0) {
      out.push({
        id: openAssistant.id,
        role: 'assistant',
        content,
        timestamp: openAssistant.timestamp,
        ...(openAssistant.attachments.length > 0
          ? { attachments: openAssistant.attachments }
          : {}),
      });
    }
    openAssistant = null;
  };

  rows.forEach((row, index) => {
    const role = row.role;
    if (role == null) return;
    if (!RENDERED_ROLES.has(role)) {
      // A tool row does NOT break the turn: it sits between two assistant
      // steps of one answer, and closing here would split that answer into a
      // bubble per step. What it DOES do is name a step of that answer.
      //
      // The agent's record holds every tool it ran, and this code used to
      // throw all of it away and rely on a device-side copy built from live
      // frames instead — so the detail existed only on the tab that watched
      // the run, and vanished from that tab the moment the chat was re-read.
      if (!isToolRow(role)) return;
      const name = toolNameFromRow(row);
      if (name == null) return;
      // An answer can open with a tool call, before it has said anything, so
      // the turn is opened here rather than only by assistant text.
      if (openAssistant == null) {
        openAssistant = {
          id:
            row.__openclaw?.id ??
            `gw-tools-${row.timestamp ?? 0}-${rowFingerprint(
              role,
              toolNameFromRow(row) ?? '',
            )}-${index}`,
          timestamp: row.timestamp ?? 0,
          parts: [],
          attachments: [],
          tools: [] as string[],
        };
      }
      openAssistant.tools.push(name);
      return;
    }
    const { text, attachments } = readGatewayRowContent(row);
    // A stable fallback, not `Date.now()`. A row missing both a timestamp and
    // an id used to be stamped with the moment it was READ and identified by
    // it — so the same row got a new identity on every read (defeating the
    // duplicate check that splices scroll-back pages together) and carried a
    // "now" time into the middle of a conversation, where it later served as
    // the boundary a page was compared against. Its position in the answer is
    // the only honest thing available.
    const timestamp = row.timestamp ?? 0;
    // The content is in the fallback identity, not just the position: see
    // `rowFingerprint`. Position stays as the tiebreaker for two rows that
    // really are identical within one page.
    const id =
      row.__openclaw?.id ??
      `gw-${timestamp}-${rowFingerprint(role, text)}-${index}`;

    if (role === 'user') {
      const visible = stripOutboundFraming(text, framing);
      // The operator-identity write contributes no bubble — and, crucially,
      // does not END the answer it landed in the middle of. It is re-sent on
      // every reconnect, so it arrives mid-turn in exactly the dropped-socket
      // case this module exists to repair: closing the turn first split one
      // answer into two bubbles, the second a duplicate of its own tail.
      if (visible.startsWith(SESSION_CONTEXT_PREFIX)) return;
      closeAssistant();
      // Nor does a turn that was nothing but framing — but "no text" is not
      // "nothing": a send with only an image attached puts framing plus an
      // image block on the wire, so its visible text is empty while the turn
      // is real. Dropping it here projected the turn to nothing, and a
      // complete read then replaced the local bubble with that nothing, so
      // the user's own image disappeared from the chat on reconnect. Same
      // test the assistant side already applies in `closeAssistant`.
      if (
        visible.trim().length === 0 &&
        (attachments == null || attachments.length === 0)
      ) {
        return;
      }
      out.push({
        id,
        role: 'user',
        content: visible,
        timestamp,
        ...(attachments != null ? { attachments } : {}),
      });
      return;
    }

    // A turn opened by a tool call keeps the identity it already has: the
    // bubble is the same answer, and re-keying it on its first words would
    // remount it mid-conversation.
    if (openAssistant == null) {
      openAssistant = {
        id,
        timestamp,
        parts: [],
        attachments: [],
        tools: [] as string[],
      };
    }
    const turn = openAssistant;
    if (text.trim().length > 0) turn.parts.push(text.trim());
    if (attachments != null) turn.attachments.push(...attachments);
  });
  closeAssistant();
  return out;
}

/*
 * The RECONCILIATION that used to live here is gone, and the whole file is
 * shorter for it.
 *
 * It merged the browser's own copy of a conversation against the agent's,
 * deciding bubble by bubble whether to keep, overwrite or delete — because
 * the local copy held both the conversation AND items only this device knew
 * about (a failed send, a widget, an approval card, a tool log, a reasoning
 * trace, an image thumbnail). Every one of those needed an exemption from
 * deletion, every exemption needed a way to recognise the thing, and each
 * recognition was a guess about a shape: text that happens to start
 * "Approve:", a bubble with no words, a local copy that is a prefix of the
 * gateway's. Wrong guesses deleted real messages.
 *
 * There is nothing to reconcile now. The agent's answer to `chat.history` is
 * the conversation; `projectGatewayHistory` above turns its rows into
 * bubbles; the browser caches that answer verbatim for the next first paint
 * and replaces it wholesale on the next read. Everything else a tab shows —
 * the answer streaming right now, a message that failed to send, a
 * background chat's live tail — is memory that lives and dies with the tab.
 */
